/**
 * SlideOverPanel — shared right-side slide-over shell.
 *
 * `placement="primary"` renders the panel flush to the right edge with a
 * dimmed backdrop (the default detail-panel behaviour). `placement="secondary"`
 * renders the panel offset to the *left* of the primary so additional linked
 * records can stack side-by-side with the primary panel staying interactive.
 *
 * For stacked secondaries (a chain of "open this linked record, then another
 * one from it"), pass `stackIndex` — 1 sits directly next to the primary, 2
 * to the left of that, etc. Each step offsets the panel by `PANEL_WIDTH`.
 *
 * Escape closes the topmost (highest stackIndex) panel only, so the user can
 * peel them off one at a time without dismissing everything by accident.
 */

import { useEffect, useState } from "react";
import { cn } from "@/utils/misc";

/** Width of one panel; secondaries are offset by this amount per stack step. */
const PANEL_WIDTH_REM = 28;

interface SlideOverPanelProps {
  open: boolean;
  onClose: () => void;
  placement?: "primary" | "secondary";
  /**
   * Position in the secondary stack. `1` is the panel closest to the primary
   * (the historical single-secondary case). `2`, `3`, ... stack further left.
   * Ignored when `placement === "primary"`.
   */
  stackIndex?: number;
  children: React.ReactNode;
}

export function SlideOverPanel({
  open,
  onClose,
  placement = "primary",
  stackIndex = 1,
  children,
}: SlideOverPanelProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      const id = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(id);
    }
    setVisible(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  const isSecondary = placement === "secondary";
  // Z-index: primary uses 50; secondaries should sit below the primary's
  // shadow but above each other, with the leftmost (highest stackIndex) on
  // top so its border is visible. Cap to avoid wild numbers.
  const z = isSecondary ? 49 - Math.min(stackIndex, 10) : 50;

  return (
    <>
      {/* Backdrop — primary only; secondary panels must not block the primary. */}
      {!isSecondary && (
        <div
          className={cn(
            "fixed inset-0 z-40 transition-opacity duration-200",
            visible
              ? "bg-black/18 dark:bg-black/40"
              : "pointer-events-none bg-transparent",
          )}
          onClick={onClose}
        />
      )}

      {/* Panel */}
      <div
        className={cn(
          "fixed top-0 h-full w-full overflow-y-auto overflow-x-hidden border-l bg-background shadow-xl transition-transform duration-200 ease-out",
          visible ? "translate-x-0" : "translate-x-full",
        )}
        style={{
          maxWidth: `${PANEL_WIDTH_REM}rem`,
          right: isSecondary ? `${stackIndex * PANEL_WIDTH_REM}rem` : 0,
          zIndex: z,
        }}
      >
        {children}
      </div>
    </>
  );
}
